import { NavLink, Outlet } from "react-router";
import { cn } from "@headerhawk/ui";
import { apiKeysEnabled } from "@/lib/feature-flags";
import { useSiteScope, withSiteScope } from "@/lib/hooks/use-site-scope";

interface SettingsNavItem {
  label: string;
  href: string;
}

/**
 * The settings sections, in order. API keys (task 437) is listed only in a
 * build with `VITE_API_KEYS_ENABLED`, and its route exists only there too.
 */
function settingsNavItems(): SettingsNavItem[] {
  return [
    { label: "General", href: "/dashboard/settings" },
    { label: "Team", href: "/dashboard/settings/team" },
    { label: "Sites", href: "/dashboard/settings/sites" },
    { label: "Authentication", href: "/dashboard/settings/authentication" },
    ...(apiKeysEnabled()
      ? [{ label: "API keys", href: "/dashboard/settings/api-keys" }]
      : []),
    { label: "Integrations", href: "/dashboard/settings/integrations" },
    { label: "Billing", href: "/dashboard/settings/billing" },
  ];
}

export function SettingsLayout() {
  const settingsNav = settingsNavItems();
  // The header's scope travels with the links, as every section's does.
  const { siteId } = useSiteScope();
  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account and site settings.
        </p>
      </div>

      <div className="flex gap-8">
        {/* Settings Navigation */}
        <nav className="hidden w-48 shrink-0 md:block">
          <ul className="space-y-1">
            {settingsNav.map((item) => (
              <li key={item.href}>
                <NavLink
                  to={withSiteScope(item.href, siteId)}
                  end={item.href === "/dashboard/settings"}
                  className={({ isActive }) =>
                    cn(
                      "block rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile Navigation */}
        <div className="mb-4 flex gap-2 md:hidden">
          {settingsNav.map((item) => (
            <NavLink
              key={item.href}
              to={withSiteScope(item.href, siteId)}
              end={item.href === "/dashboard/settings"}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
